/**
 * "Ideas for you" — the work prompts Williams offers on a blank thread.
 *
 * One pool, one picker, three consumers (ui-ux-guideline §2.6 item 3,
 * DECISIONS 2026-09-17 "The staff home is the copilot", revised 2026-09-18
 * "Three ideas from a pool"): the Williams page renders the draw under the
 * composer, the TRFer home renders the same draw under its hero composer,
 * and the widget's `/trfer` chip row reads a day-seeded draw's labels.
 *
 * Until 2026-09-18 this was five fixed rows in a fixed order, and the same
 * five on every visit stopped being read. Victor: "reduce the number of
 * ideas from 5 to 3 … make sure some ideas are grant, application or
 * reimbursement focused … make sure the ideas rotate and that we have a good
 * pool of ideas to draw from." So: a pool of twelve, `IDEAS_SHOWN` drawn
 * fresh on every mount by `pickIdeas`, and **every draw carries a grant-desk
 * idea and a caseload idea**. The escalations idea left with §2.7's vocabulary —
 * escalations are unread messages on the sidebar, not a prompt.
 *
 * Every grant-desk idea has a read behind it: Williams' `get_grant_desk`
 * tool (agents/williams) reads the same collections the Applications,
 * Reimbursements and Grant requests pages read. An idea Williams cannot
 * answer from data is never offered — a prompt that fails on first press is
 * the dead-guard shape.
 */
import type { LucideIcon } from 'lucide-react';
import {
  CalendarClock,
  ClipboardList,
  FileQuestion,
  FileSignature,
  HandCoins,
  ListTodo,
  PenLine,
  PhoneCall,
  Receipt,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';

/** What the idea is about. `grant` = the grant desk (applications, letters, money); `caseload` = the families. */
export type IdeaTheme = 'grant' | 'caseload';

export interface IdeaSpec {
  id: string;
  theme: IdeaTheme;
  icon: LucideIcon;
  /** The row's words — also what is sent when nothing else is given. */
  label: string;
  /** The text posted to Williams when the row is picked. */
  message: string;
}

/** How many ideas a blank thread shows. */
export const IDEAS_SHOWN = 3;

export const WILLIAMS_IDEA_POOL: readonly IdeaSpec[] = [
  // ── The grant desk ────────────────────────────────────────────────────
  {
    id: 'applications-waiting-longest',
    theme: 'grant',
    icon: ClipboardList,
    label: 'Which applications have waited longest for a review?',
    message: 'Which applications have waited longest for a review?',
  },
  {
    id: 'applications-needing-more',
    theme: 'grant',
    icon: FileQuestion,
    label: 'Which applications still need something from the family?',
    message: 'Which applications still need something from the family?',
  },
  {
    id: 'admission-letters-unsigned',
    theme: 'grant',
    icon: FileSignature,
    label: 'Which welcome emails are still waiting on an acceptance?',
    message: 'Which families have been sent the welcome email but have not accepted yet, and for how long?',
  },
  {
    id: 'reimbursements-waiting',
    theme: 'grant',
    icon: Receipt,
    label: 'Which reimbursements are waiting on a decision?',
    message: 'Which reimbursement claims from my families are waiting on a decision?',
  },
  {
    id: 'grant-requests-open',
    theme: 'grant',
    icon: HandCoins,
    label: 'Which grant requests are still open?',
    message: 'Which grant requests from my families are still open?',
  },
  {
    id: 'renewals-underway',
    theme: 'grant',
    icon: CalendarClock,
    label: 'Which renewals are underway?',
    message: 'Which renewal applications are underway, and where does each stand?',
  },
  {
    id: 'grant-standing-by-family',
    theme: 'grant',
    icon: Wallet,
    label: 'Where does each family’s grant stand?',
    message: 'Summarize where each of my families’ grants stands.',
  },
  // ── The caseload ──────────────────────────────────────────────────────
  {
    id: 'prep-next-call',
    theme: 'caseload',
    icon: PhoneCall,
    label: 'Prep me for my next family call',
    message: 'Prep me for my next family call',
  },
  {
    id: 'families-needing-attention',
    theme: 'caseload',
    icon: Users,
    label: 'Which families need my attention today?',
    message: 'Which families need my attention today?',
  },
  {
    id: 'draft-notes-last-call',
    theme: 'caseload',
    icon: PenLine,
    label: 'Draft notes from my last call',
    message: 'Draft notes from my last call',
  },
  {
    id: 'caseload-patterns',
    theme: 'caseload',
    icon: TrendingUp,
    label: 'What patterns am I seeing across my caseload?',
    message: 'What patterns am I seeing across my caseload?',
  },
  {
    id: 'tasks-unclaimed',
    theme: 'caseload',
    icon: ListTodo,
    label: 'Which tasks has nobody picked up?',
    message: 'Which open tasks across my families has nobody picked up?',
  },
];

/** mulberry32 — a tiny seeded PRNG, so a draw is reproducible from its seed. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffle<T>(items: readonly T[], rand: () => number): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Draw `count` ideas from the pool for one visit.
 *
 * Deterministic in `seed`, so a test can pin a draw and two consumers on one
 * page can agree. The rule the draw keeps: **one grant-desk idea and one
 * caseload idea** whenever the pool has each (the first local walk drew three
 * grant ideas twice running — allowed by "at least one grant", and it read as
 * a grant menu). One slot from each theme, the rest from everything left,
 * then the whole draw shuffled so neither theme is always on top. Order
 * within the result is part of the draw.
 */
export function pickIdeas(
  pool: readonly IdeaSpec[],
  seed: number,
  count: number = IDEAS_SHOWN,
): IdeaSpec[] {
  if (count <= 0 || pool.length === 0) return [];
  const rand = mulberry32(seed);
  const picked: IdeaSpec[] = [];
  for (const theme of ['grant', 'caseload'] as const) {
    if (picked.length >= count) break;
    const ofTheme = shuffle(pool.filter((i) => i.theme === theme), rand);
    if (ofTheme.length > 0) picked.push(ofTheme[0]);
  }
  const rest = shuffle(pool.filter((i) => !picked.includes(i)), rand);
  for (const idea of rest) {
    if (picked.length >= count) break;
    picked.push(idea);
  }
  return shuffle(picked.slice(0, count), rand);
}

/** A fresh seed for one mount. */
export function freshSeed(): number {
  return Math.floor(Math.random() * 0x7fffffff);
}

/** One seed per calendar day (local), for a consumer that draws once per page load. */
export function daySeed(now: Date = new Date()): number {
  return now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
}
